/**
 * Iter 130 — Workspace Integrations UI.
 * Iter 162 — Added Resend (email) card + "Send test message" verify buttons
 * on Interakt + Resend cards. Same Interakt key now powers Onboarding Kits,
 * Dunning ladder sends, Quote nudges — everything routed through
 * messaging_service.send_message().
 *
 * Backend endpoints:
 *   GET/PUT/DELETE /workspaces/{ws_id}/integrations/interakt
 *   GET/PUT/DELETE /workspaces/{ws_id}/integrations/resend
 *   POST          /workspaces/{ws_id}/integrations/test-send
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Save, Trash2, MessageCircle, Phone, Mail, Send, Search, TrendingUp, Zap, RefreshCw, Plus } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import ImapMailboxSection from "@/components/settings/ImapMailboxSection";
import MailboxPolicySection from "@/components/settings/MailboxPolicySection";
import DndCard from "@/components/settings/DndCard";

export default function IntegrationsSection() {
  return (
    <div className="space-y-6" data-testid="settings-integrations-section">
      <EmailProviderCard />
      <ImapMailboxSection />
      <MailboxPolicySection />
      <ResendCard />
      <InteraktCard />
      <MCubeCard />
      <DndCard />
      <DataForSEOCard />
      <GoogleOAuthCard />
      <PageSpeedCard />
      <LogNotificationCard />
      <div className="border border-dashed border-border p-5 text-xs text-muted-foreground">
        <p className="font-semibold text-foreground/80 mb-1">More providers</p>
        <p>Twilio (WhatsApp/SMS) + Apollo / Proxycurl / Serper can be configured under Marketing → Providers and Prospecting → Sources. Payments (Stripe / Cashfree / Razorpay) are configured under Billing.</p>
      </div>
    </div>
  );
}

// ─────────────── R58 · Multi-provider email routing (Resend + ZeptoMail) ───────────────
//
// Lets workspace admins pick which transactional-email provider to use
// (Resend, ZeptoMail, or "use platform default"). Keys for BOTH providers
// can be stored so switching is a one-click toggle. Existing per-workspace
// Resend key (below) stays for legacy marketing campaign flows.

function EmailProviderCard() {
  const { activeWorkspaceId } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    provider: "platform_default",
    sender_email: "",
    sender_name: "",
    resend_api_key: "",
    zeptomail_api_key: "",
    zeptomail_region: "us",
    // R47 · Generic SMTP (BigRock, HostGator, cPanel, Zoho SMTP, etc)
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
  });
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/settings/email-provider`)
      .then((r) => {
        setCfg(r.data);
        setForm((f) => ({
          ...f,
          provider: r.data?.provider || "platform_default",
          sender_email: r.data?.sender_email || "",
          sender_name: r.data?.sender_name || "",
          zeptomail_region: r.data?.zeptomail_region || "us",
          // R47 · SMTP fields (never returns the password itself)
          smtp_host: r.data?.smtp_host || "",
          smtp_port: r.data?.smtp_port || 587,
          smtp_username: r.data?.smtp_username || "",
          smtp_use_tls: r.data?.smtp_use_tls !== false,
        }));
      })
      .catch(() => setCfg({}));
  };
  useEffect(load, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (form.provider === "resend" && !cfg?.has_resend_key && !form.resend_api_key.trim()) {
      toast.error("Paste your Resend API key first (starts with re_)"); return;
    }
    if (form.provider === "zeptomail" && !cfg?.has_zeptomail_key && !form.zeptomail_api_key.trim()) {
      toast.error("Paste your ZeptoMail Send-Mail Token first"); return;
    }
    if (form.provider === "smtp") {
      if (!form.smtp_host.trim()) { toast.error("SMTP host is required (e.g. smtp.hostgator.com, smtp.bigrock.in)"); return; }
      if (!form.smtp_username.trim()) { toast.error("SMTP username (usually the full email address) is required"); return; }
      if (!cfg?.has_smtp_password && !form.smtp_password.trim()) { toast.error("SMTP password required on first save"); return; }
    }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/email-provider`, {
        provider: form.provider,
        sender_email: form.sender_email.trim() || null,
        sender_name: form.sender_name.trim() || null,
        resend_api_key: form.resend_api_key.trim() || null,
        zeptomail_api_key: form.zeptomail_api_key.trim() || null,
        zeptomail_region: form.zeptomail_region,
        smtp_host: form.smtp_host.trim() || null,
        smtp_port: form.smtp_port || null,
        smtp_username: form.smtp_username.trim() || null,
        smtp_password: form.smtp_password.trim() || null,
        smtp_use_tls: form.smtp_use_tls,
      });
      toast.success("Email provider config saved (keys encrypted at rest)");
      setForm({ ...form, resend_api_key: "", zeptomail_api_key: "", smtp_password: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save email provider config")); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a recipient email first"); return; }
    setTesting(true);
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/settings/email-provider/test`,
        { to: testEmail.trim() },
      );
      // R268rr · Feb 2026 — Surface silent-fallback honestly. If the
      // backend fell back to the platform default because the chosen
      // provider's creds were incomplete, show a warning toast with
      // the exact reason instead of the misleading "sent via Resend".
      const src = r.data?.source;
      const prov = r.data?.provider || "unknown provider";
      if (src === "platform_fallback_missing_creds") {
        toast.warning(
          `Sent via platform default (${prov}) — ${r.data?.fallback_reason || "your chosen provider's config was incomplete"}. Fix the missing field and try again.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Test email sent via ${prov}`);
      }
    } catch (e) {
      // R268rr · The backend now returns 400 with a `reason` field when
      // the chosen provider's config is incomplete. Show that reason
      // verbatim so the admin knows WHICH field to fill in.
      const detail = e?.response?.data?.detail;
      const reason = detail?.reason || detail?.message;
      if (reason) toast.error(reason, { duration: 8000 });
      else toast.error(apiErrorMessage(e, "Test send failed"));
    }
    finally { setTesting(false); load(); }
  };

  const runDiagnose = async () => {
    setDiagnosing(true); setDiagResult(null);
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/settings/email-provider/diagnose`,
      );
      setDiagResult(r.data);
      if (r.data?.overall_ok) toast.success("All checks passed");
      else toast.warning(r.data?.conclusion || "Diagnose found an issue");
    } catch (e) { toast.error(apiErrorMessage(e, "Diagnose failed")); }
    finally { setDiagnosing(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground py-6">Loading email provider…</div>;

  const usingPlatform = form.provider === "platform_default";

  return (
    <div className="border border-border p-6 space-y-4" data-testid="integrations-email-provider-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-purple-50">
          <Mail className="w-5 h-5 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Transactional Email Provider</div>
          <p className="text-xs text-muted-foreground">
            Choose which service delivers your <b>transactional emails</b> (invites, invoices, password resets, notifications).
            Store keys for both Resend and Zoho ZeptoMail so switching is a one-click toggle.
          </p>
        </div>
        <Badge variant="outline"
          className={`rounded-none ${usingPlatform ? "border-foreground/25 text-foreground/60" : "border-emerald-500/40 text-emerald-700"}`}
          data-testid="integrations-email-provider-badge">
          {usingPlatform ? "Using platform default"
            : form.provider === "zeptomail" ? "ZeptoMail"
            : form.provider === "resend" ? "Resend"
            : "SMTP"}
        </Badge>
      </div>

      {/* R268rr · Feb 2026 — Loud warning when the picked provider is
          missing creds. Without this, `Send test` silently falls back
          to the platform default and admins have no idea why their
          ZeptoMail token is being ignored. */}
      {cfg && cfg.usable === false && cfg.missing_reason && (
        <div className="border border-red-500/40 bg-red-50 text-red-800 px-3 py-2 text-xs flex items-start gap-2"
             data-testid="email-provider-not-usable-warning">
          <span className="font-semibold">⚠ Config incomplete:</span>
          <span className="flex-1">
            {cfg.missing_reason}. Fill this in and press <b>Save</b> — until
            then, real sends will silently fall back to the platform default
            and test sends will be refused.
          </span>
        </div>
      )}

      <div className="grid grid-cols-4 gap-2">
        {[
          { v: "platform_default", label: "Platform default", hint: "Fallback to Super Admin config" },
          { v: "resend", label: "Resend", hint: cfg.has_resend_key ? "✓ Key saved" : "No key" },
          { v: "zeptomail", label: "ZeptoMail (Zoho)", hint: cfg.has_zeptomail_key ? "✓ Key saved" : "No key" },
          { v: "smtp", label: "SMTP (BigRock, HostGator, cPanel, Zoho)", hint: cfg.has_smtp_password ? "✓ Configured" : "Not configured" },
        ].map((opt) => (
          <button key={opt.v} type="button"
            onClick={() => setForm({ ...form, provider: opt.v })}
            data-testid={`email-provider-radio-${opt.v}`}
            className={`text-left p-3 border rounded-none transition-colors ${
              form.provider === opt.v
                ? "border-purple-600 bg-purple-50"
                : "border-border hover:bg-secondary/40"
            }`}>
            <div className="font-semibold text-sm">{opt.label}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{opt.hint}</div>
          </button>
        ))}
      </div>

      {!usingPlatform && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Sender email (optional)</Label>
            <Input value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              placeholder="e.g. noreply@yourdomain.com"
              className="h-9 rounded-none" data-testid="email-provider-sender-email" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Sender name (optional)</Label>
            <Input value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="e.g. Acquilix Team"
              className="h-9 rounded-none" data-testid="email-provider-sender-name" />
          </div>
        </div>
      )}

      {/* Resend key */}
      <div className="border-l-2 border-orange-500/60 pl-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="label-eyebrow inline-block">Resend API key</Label>
          {cfg.has_resend_key && (
            <span className="text-[10px] text-emerald-700">✓ Key saved (encrypted)</span>
          )}
        </div>
        <Input type="password" value={form.resend_api_key}
          onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
          placeholder={cfg.has_resend_key ? "Leave blank to keep existing" : "re_..."}
          className="h-9 rounded-none font-mono text-xs"
          data-testid="email-provider-resend-key" />
      </div>

      {/* ZeptoMail key + region */}
      <div className="border-l-2 border-blue-500/60 pl-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="label-eyebrow inline-block">ZeptoMail Send-Mail Token</Label>
          {cfg.has_zeptomail_key && (
            <span className="text-[10px] text-emerald-700">✓ Key saved (encrypted)</span>
          )}
        </div>
        <Input type="password" value={form.zeptomail_api_key}
          onChange={(e) => setForm({ ...form, zeptomail_api_key: e.target.value })}
          placeholder={cfg.has_zeptomail_key ? "Leave blank to keep existing" : "Zoho-enczapikey token"}
          className="h-9 rounded-none font-mono text-xs"
          data-testid="email-provider-zeptomail-key" />
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">Region:</span>
          {[
            { v: "us", label: "US (.com)" },
            { v: "eu", label: "EU (.eu)" },
            { v: "in", label: "IN (.in)" },
          ].map((r) => (
            <label key={r.v} className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={form.zeptomail_region === r.v}
                onChange={() => setForm({ ...form, zeptomail_region: r.v })}
                data-testid={`email-provider-zepto-region-${r.v}`} />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">
          Get your Send-Mail Token from ZeptoMail dashboard → Mail Agents → your Agent → SMTP/API → API tab.
        </p>
      </div>

      {/* R47 · Generic SMTP — BigRock, HostGator, cPanel Webmail, Zoho, etc */}
      <div className="border-l-2 border-emerald-500/60 pl-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="label-eyebrow inline-block">SMTP — BigRock / HostGator / cPanel / Zoho</Label>
          {cfg.has_smtp_password && <span className="text-[10px] text-emerald-700">✓ Password saved (encrypted)</span>}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-[10px] text-muted-foreground">SMTP host</Label>
            <Input value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              placeholder="smtp.hostgator.com · smtp.bigrock.in · mail.yourdomain.com"
              className="h-9 rounded-none font-mono text-xs"
              data-testid="email-provider-smtp-host" />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Port</Label>
            <Input type="number" value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value, 10) || 587 })}
              placeholder="587"
              className="h-9 rounded-none font-mono text-xs"
              data-testid="email-provider-smtp-port" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">Username (usually full email)</Label>
            <Input value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
              placeholder="admin@yourdomain.com"
              className="h-9 rounded-none font-mono text-xs"
              data-testid="email-provider-smtp-username" />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Password</Label>
            <Input type="password" value={form.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              placeholder={cfg.has_smtp_password ? "Leave blank to keep existing" : "mailbox password"}
              className="h-9 rounded-none font-mono text-xs"
              data-testid="email-provider-smtp-password" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-[11px] cursor-pointer">
          <input type="checkbox" checked={form.smtp_use_tls}
            onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })}
            data-testid="email-provider-smtp-tls" />
          Use STARTTLS (recommended — port 587)
        </label>
        <details className="text-[10px] text-muted-foreground border border-dashed border-emerald-500/40 p-2">
          <summary className="cursor-pointer text-emerald-700 font-semibold">Where do I find these? (BigRock · HostGator · cPanel · Zoho)</summary>
          <div className="pt-1 space-y-1 leading-relaxed">
            <p><b>BigRock</b>: cPanel → <i>Email Accounts</i> → click your mailbox → <i>Connect Devices</i> → <i>Manual settings</i>. Copy SMTP host + port 587 (STARTTLS).</p>
            <p><b>HostGator</b>: cPanel → <i>Email Accounts</i> → your mailbox → <i>Connect Devices</i>. Non-SSL: host <code>mail.yourdomain.com</code>, port <code>587</code>, username = full email address, password = mailbox password.</p>
            <p><b>Zoho SMTP</b>: <code>smtp.zoho.in</code> · port <code>587</code> · TLS ON · username = full Zoho email · password = Zoho app-password (generate at accounts.zoho.com → Security → App Passwords).</p>
            <p><b>Gmail SMTP</b>: <code>smtp.gmail.com</code> · port <code>587</code> · TLS ON · username = your Gmail · password = an app-password (not your Google login).</p>
            <p className="italic">All replies land in this mailbox — Acquilix sets Reply-To to the sending user's email so responses come back to them.</p>
          </div>
        </details>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button onClick={save} disabled={busy} data-testid="email-provider-save"
          className="rounded-none">
          {busy ? "Saving…" : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
        </Button>
        <Button variant="outline" onClick={runDiagnose} disabled={diagnosing}
          className="rounded-none" data-testid="email-provider-diagnose">
          {diagnosing ? "Checking…" : "Diagnose connection"}
        </Button>
        <div className="flex-1" />
        <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-9 rounded-none max-w-[220px]"
          data-testid="email-provider-test-recipient" />
        <Button variant="outline" onClick={runTest} disabled={testing}
          className="rounded-none" data-testid="email-provider-test-send">
          {testing ? "Sending…" : <><Send className="w-4 h-4 mr-1.5" /> Send test</>}
        </Button>
      </div>

      {diagResult && (
        <div className={`border-2 p-3 mt-2 rounded-none ${diagResult.overall_ok ? "border-emerald-500/40 bg-emerald-50" : "border-rose-500/60 bg-rose-50"}`}
             data-testid="email-provider-diagnose-result">
          <div className="text-xs font-bold flex items-center gap-1.5 mb-1">
            {diagResult.overall_ok ? "✓ Connection healthy" : "✗ Issue detected"}
            <span className="text-[10px] font-normal text-muted-foreground">
              · {diagResult.host} · region {diagResult.region}
            </span>
          </div>
          <div className="text-xs mb-2">{diagResult.conclusion}</div>
          <div className="space-y-0.5">
            {(diagResult.steps || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className={s.ok ? "text-emerald-700" : "text-rose-700"}>
                  {s.ok ? "✓" : "✗"}
                </span>
                <span className="font-mono w-28">{s.name}</span>
                <span className="text-muted-foreground w-16">{s.duration_ms}ms</span>
                {s.status_code && <span className="text-muted-foreground">HTTP {s.status_code}</span>}
                {s.error && <span className="text-rose-700 truncate max-w-[300px]" title={s.error}>{s.error}</span>}
                {s.resolved_ips && <span className="text-muted-foreground text-[10px] font-mono">{s.resolved_ips.join(", ")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────── Log Notification config (Iter 209 Part 2) ───────────────

function LogNotificationCard() {
  const { activeWorkspaceId } = useAuth();
  const [cfg, setCfg] = useState({ enabled: false, notify_on_add: true, notify_on_update: true, recipient_emails: [] });
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/log/notification-config`)
      .then((r) => {
        setCfg({
          enabled: !!r.data?.enabled,
          notify_on_add: r.data?.notify_on_add !== false,
          notify_on_update: r.data?.notify_on_update !== false,
          recipient_emails: r.data?.recipient_emails || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeWorkspaceId]);

  const addEmail = () => {
    const v = emailInput.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("Invalid email"); return; }
    if (cfg.recipient_emails.includes(v)) { setEmailInput(""); return; }
    setCfg((c) => ({ ...c, recipient_emails: [...c.recipient_emails, v] }));
    setEmailInput("");
  };
  const removeEmail = (e) => setCfg((c) => ({ ...c, recipient_emails: c.recipient_emails.filter((x) => x !== e) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/log/notification-config`, cfg);
      toast.success("Log notifications saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border p-5" data-testid="settings-log-notification-card">
      <div className="flex items-center gap-3 mb-3">
        <KeyRound className="w-5 h-5 text-foreground/70" />
        <div>
          <div className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Log module — email notifications</div>
          <p className="text-xs text-muted-foreground">Fire an email whenever anyone adds or updates a Log item (risks · actions · decisions · lessons · opportunities · status updates and more).</p>
        </div>
      </div>
      {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} data-testid="log-notif-enabled" />
            Enabled
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={cfg.notify_on_add} onChange={(e) => setCfg((c) => ({ ...c, notify_on_add: e.target.checked }))} data-testid="log-notif-add" />
              Notify on add
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={cfg.notify_on_update} onChange={(e) => setCfg((c) => ({ ...c, notify_on_update: e.target.checked }))} data-testid="log-notif-update" />
              Notify on update
            </label>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Recipient emails</Label>
            <div className="flex gap-2 mb-2">
              <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                     placeholder="ops@studio.io"
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                     className="h-9 rounded-none text-xs" data-testid="log-notif-email-input" />
              <Button type="button" onClick={addEmail} variant="outline" className="rounded-none h-9" data-testid="log-notif-email-add">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cfg.recipient_emails.map((e) => (
                <Badge key={e} className="rounded-none gap-1" variant="outline" data-testid={`log-notif-email-chip-${e}`}>
                  <Mail className="w-3 h-3" /> {e}
                  <button type="button" onClick={() => removeEmail(e)} className="ml-1 opacity-60 hover:opacity-100" data-testid={`log-notif-email-remove-${e}`}>×</button>
                </Badge>
              ))}
              {cfg.recipient_emails.length === 0 && <span className="text-xs text-meta">No recipients configured.</span>}
            </div>
          </div>
          <div>
            <Button onClick={save} disabled={saving} className="rounded-none" data-testid="log-notif-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────── Test-send helper ───────────────

function TestSendButton({ provider, wsId, testid }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 253z-4 · Template picker + message-type selector for Interakt.
  const [templates, setTemplates] = useState([]);
  const [templatesMeta, setTemplatesMeta] = useState(null);  // {source, reason, configured, status, raw}
  const [refetching, setRefetching] = useState(false);
  const [templateName, setTemplateName] = useState("__session__");
  const [bodyVars, setBodyVars] = useState("");
  const label = provider === "resend" ? "Send test email" : "Send test WhatsApp";
  const placeholder = provider === "resend" ? "you@example.com" : "+91XXXXXXXXXX";
  const fieldKey = provider === "resend" ? "to_email" : "to_phone";

  // Load Interakt templates once the picker opens.
  const loadTemplates = async () => {
    if (!wsId || provider !== "interakt") return;
    setRefetching(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/integrations/interakt/templates`);
      setTemplates(data?.rows || []);
      setTemplatesMeta(data || null);
    } catch { setTemplates([]); setTemplatesMeta({ source: "error", reason: "Fetch failed" }); }
    finally { setRefetching(false); }
  };
  useEffect(() => {
    if (!open || provider !== "interakt" || !wsId) return;
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider, wsId]);

  const fire = async () => {
    if (!to.trim()) { toast.error("Enter a destination"); return; }
    setBusy(true);
    try {
      const payload = { provider, [fieldKey]: to.trim() };
      if (provider === "interakt" && templateName && templateName !== "__session__") {
        payload.template_name = templateName;
        const vars = bodyVars.split(",").map((s) => s.trim()).filter(Boolean);
        if (vars.length) payload.template_body_vars = vars;
      }
      const { data } = await api.post(
        `/workspaces/${wsId}/integrations/test-send`, payload);
      const overall = data?.result?.overall;
      const leg = provider === "resend" ? data?.result?.email : data?.result?.whatsapp;
      const reason = leg?.reason || "";
      if (overall === "sent") {
        toast.success(`Test sent — check ${to} in the next minute`);
        setOpen(false);
        setTo("");
      } else if (overall === "skipped") {
        // "no_recipients" / "resend_not_configured" / "no_wa_provider_configured"
        // are the possible reasons — pick a friendly message per case.
        if (reason.includes("not_configured") || reason.includes("no_wa_provider")) {
          toast.error("Provider not configured — save your API key first");
        } else if (reason === "no_recipients") {
          toast.error("Missing recipient");
        } else {
          toast.error(`Send skipped: ${reason || "provider did not attempt delivery"}`);
        }
      } else {
        toast.error(`Send failed: ${reason || "unknown provider error"}`);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Test send failed")); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}
               className="rounded-none ml-2" data-testid={testid}>
        <Send className="w-3 h-3 mr-1" /> {label}
      </Button>
    );
  }
  return (
    <div className="border border-dashed border-border p-2 space-y-2">
      <div className="flex items-center gap-2">
        <Input value={to} onChange={(e) => setTo(e.target.value)}
                placeholder={placeholder}
                className="h-8 rounded-none font-mono text-xs w-56"
                data-testid={`${testid}-input`} />
        <Button size="sm" onClick={fire} disabled={busy}
                 className="rounded-none" data-testid={`${testid}-fire`}>
          {busy ? "Sending…" : "Send"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setTo(""); }}
                 className="rounded-none">Cancel</Button>
      </div>
      {provider === "interakt" && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
              Message type
            </label>
            <select className="h-8 px-2 border border-border bg-background text-xs"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      data-testid={`${testid}-template-picker`}>
              <option value="__session__">Session (free-form · 24h window)</option>
              {templates.map((t) => (
                <option key={t.name || t.id} value={t.name || t.template_name}>
                  {(t.name || t.template_name)}
                  {t.language ? ` · ${t.language}` : ""}
                  {t.category ? ` [${t.category}]` : ""}
                </option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={loadTemplates} disabled={refetching}
                     className="rounded-none h-8 text-[11px]" data-testid={`${testid}-refetch-templates`}
                     title="Re-fetch templates from Interakt">
              <RefreshCw className={`w-3 h-3 mr-1 ${refetching ? "animate-spin" : ""}`} />
              Refetch
            </Button>
            {templateName !== "__session__" && (
              <Input value={bodyVars} onChange={(e) => setBodyVars(e.target.value)}
                      placeholder="Body vars, comma-separated (e.g. Alice, 20 Feb)"
                      className="h-8 rounded-none text-[11px] flex-1 min-w-[220px]"
                      data-testid={`${testid}-body-vars`} />
            )}
          </div>
          {templatesMeta && templatesMeta.source !== "interakt" && (
            <div className={`text-[10px] px-2 py-1 border ${
              templatesMeta.source === "mock" ? "border-amber-400 bg-amber-50/40 text-amber-800"
                : templatesMeta.source === "no_public_api" ? "border-sky-400 bg-sky-50/40 text-sky-800"
                : "border-rose-400 bg-rose-50/40 text-rose-800"
            }`} data-testid={`${testid}-template-source`}>
              <b className="uppercase mr-1">
                {templatesMeta.source === "mock" ? "Mock templates" :
                  templatesMeta.source === "empty" ? "No approved templates" :
                  templatesMeta.source === "no_public_api" ? "Interakt limitation" :
                  "Fetch error"}
              </b>
              {templatesMeta.reason}
              {templatesMeta.status && ` (HTTP ${templatesMeta.status})`}
            </div>
          )}
          {templatesMeta && templatesMeta.source === "interakt" && templates.length > 0 && (
            <div className="text-[10px] text-emerald-700" data-testid={`${testid}-template-source`}>
              ✓ Loaded {templates.length} template{templates.length !== 1 ? "s" : ""} from Interakt.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────── Interakt one-click hello_world tester (Feb 2026 R6) ───────────────

function InteraktHelloWorldTest({ wsId }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(() => localStorage.getItem("interakt-hello-test-phone") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {status, provider, http, detail, template}

  const send = async () => {
    const clean = phone.trim();
    if (!clean.match(/^\+?\d{7,}$/)) {
      toast.error("Enter a phone number in E.164 format (e.g. +919999999999)");
      return;
    }
    localStorage.setItem("interakt-hello-test-phone", clean);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/integrations/test-send`,
        { provider: "interakt", to_phone: clean, template_name: "hello_world" },
      );
      const leg = data?.result?.whatsapp || {};
      const overall = data?.result?.overall;
      setResult({
        overall,
        status: leg.status,
        reason: leg.reason,
        detail: leg.detail,
        provider_status: leg.provider_status,
      });
      if (overall === "sent") {
        toast.success(`hello_world sent — check ${clean} in the next minute`);
      } else {
        toast.error(`Send did not succeed — see diagnostic panel below`);
      }
    } catch (e) {
      const msg = apiErrorMessage(e, "Test failed");
      setResult({ overall: "error", reason: "http_error", detail: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}
              className="rounded-none border-emerald-500/40 text-emerald-700"
              data-testid="integrations-interakt-hello-open">
        <Send className="w-3 h-3 mr-1" /> Test with <code className="mx-1">hello_world</code>
      </Button>
    );
  }
  return (
    <div className="border border-emerald-500/40 bg-emerald-50/30 p-3 space-y-2 w-full" data-testid="integrations-interakt-hello-panel">
      <div className="text-[11px]">
        Sends the Meta-approved <code>hello_world</code> template to the number below and
        prints the full diagnostic. Use this to validate your API key + WABA setup in one click.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+919999999999"
          className="h-8 rounded-none font-mono text-xs w-56"
          data-testid="integrations-interakt-hello-phone"
        />
        <Button size="sm" onClick={send} disabled={busy} className="rounded-none"
                data-testid="integrations-interakt-hello-send">
          {busy ? "Sending…" : "Send hello_world"}
        </Button>
        <Button size="sm" variant="ghost"
                onClick={() => { setOpen(false); setResult(null); }}
                className="rounded-none">Cancel</Button>
      </div>
      {result && (
        <div
          className={`text-[11px] p-2 border font-mono whitespace-pre-wrap break-all ${
            result.overall === "sent"
              ? "border-emerald-500 bg-emerald-50 text-emerald-900"
              : "border-rose-500 bg-rose-50 text-rose-900"
          }`}
          data-testid="integrations-interakt-hello-result"
        >
          <div className="font-semibold not-italic mb-1">
            {result.overall === "sent" ? "✓ Sent" : `✗ ${result.overall || "failed"}`}
          </div>
          {result.status && <div>status: {result.status}</div>}
          {result.reason && <div>reason: {result.reason}</div>}
          {result.provider_status && <div>interakt_http: {result.provider_status}</div>}
          {result.detail && <div>detail: {result.detail}</div>}
          {result.reason && result.reason.includes("template") && (
            <div className="mt-2 not-italic font-sans text-[11px]">
              Fix: On <a href="https://app.interakt.ai/templates/list" target="_blank" rel="noreferrer" className="underline">Interakt → Templates</a>,
              confirm <code>hello_world</code> exists AND has status <b>APPROVED</b> (not Pending / Rejected).
              If missing, use Meta&apos;s default <code>hello_world</code> which is auto-provisioned for every WABA.
            </div>
          )}
          {result.reason === "interakt_401" && (
            <div className="mt-2 not-italic font-sans text-[11px]">
              Fix: The API key is invalid. Regenerate it at{" "}
              <a href="https://app.interakt.ai/settings/developer-setting" target="_blank" rel="noreferrer" className="underline">
                Interakt → Developer Settings → API Key
              </a> and paste the new value above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────── Resend (email) card ───────────────

function ResendCard() {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/integrations/resend`)
      .then((r) => {
        setStatus(r.data);
        setFromEmail(r.data?.from_email || "");
        setFromName(r.data?.from_name || "");
      })
      .catch(() => setStatus({ configured: false }));
  };
  useEffect(load, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!apiKey.trim() || apiKey.trim().length < 10) {
      toast.error("Paste a valid Resend API key (starts with re_)"); return;
    }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/resend`, {
        api_key: apiKey.trim(),
        from_email: fromEmail.trim() || null,
        from_name: fromName.trim() || null,
      });
      toast.success("Resend key saved (encrypted at rest)");
      setApiKey("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save Resend key")); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/integrations/resend`);
      toast.success("Resend key removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to remove key")); }
  };

  if (!status) return <div className="text-sm text-muted-foreground py-6">Loading integrations…</div>;

  return (
    <div className="border border-border p-6 space-y-4" data-testid="integrations-resend-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-orange-50">
          <Mail className="w-5 h-5 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Resend · Transactional Email</div>
          <p className="text-xs text-muted-foreground">
            Powers <strong>all outbound emails</strong> — Quote sends, Invoice sends, Dunning ladder, Quote nudges, Marketing sequences.
            Without a key we still track opens (via the tracking pixel) but no actual email is delivered.
          </p>
        </div>
        <Badge variant="outline"
          className={`rounded-none ${status.configured ? "border-emerald-500/40 text-emerald-700" : "border-foreground/25 text-foreground/60"}`}
          data-testid="integrations-resend-status-badge">
          {status.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>

      {status.configured && status.updated_at && (
        <div className="text-xs text-muted-foreground border-l-2 border-orange-500/40 pl-3">
          Last updated {status.updated_at.slice(0, 16).replace("T", " ")}.
          {status.from_email && <> Sending as <code>{status.from_name ? `${status.from_name} <${status.from_email}>` : status.from_email}</code>.</>}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="label-eyebrow mb-1.5 inline-flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Resend API key
          </Label>
          <Input type="password" value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={status.configured ? "•••••• (paste to replace)" : "re_..."}
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-resend-key-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Get one at{" "}
            <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">
              resend.com → API Keys
            </a>. Domain must be verified separately at{" "}
            <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">
              resend.com/domains
            </a>.
          </p>
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">From email</Label>
          <Input value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="hello@yourdomain.com"
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-resend-from-email-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Must match a verified Resend domain.
          </p>
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">From name (optional)</Label>
          <Input value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Acquilix Team"
            className="h-9 rounded-none"
            data-testid="integrations-resend-from-name-input" />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="integrations-resend-save">
          <Save className="w-3 h-3 mr-1" />
          {busy ? "Saving…" : (status.configured ? "Replace key" : "Save key")}
        </Button>
        {status.configured && (
          <TestSendButton provider="resend" wsId={activeWorkspaceId}
                          testid="integrations-resend-test" />
        )}
        {status.configured && (
          <Button onClick={() => setConfirmOpen(true)} variant="ghost" className="rounded-none text-destructive ml-auto"
            data-testid="integrations-resend-delete">
            <Trash2 className="w-3 h-3 mr-1" /> Remove
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove Resend key?"
        description="Email delivery will stop for this workspace. Existing tracking data stays intact."
        confirmLabel="Remove key"
        destructive
        onConfirm={doRemove}
        testid="confirm-remove-resend"
      />
    </div>
  );
}

function InteraktCard() {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [tmplName, setTmplName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/integrations/interakt`)
      .then((r) => {
        setStatus(r.data);
        setTmplName(r.data?.template_name || "onboarding_welcome");
      })
      .catch(() => setStatus({ configured: false }));
  };
  useEffect(load, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!apiKey.trim() || apiKey.trim().length < 10) {
      toast.error("Paste a valid Interakt API key (min 10 chars)");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/interakt`, {
        api_key: apiKey.trim(),
        template_name: tmplName.trim() || "onboarding_welcome",
      });
      toast.success("Interakt key saved (encrypted at rest)");
      setApiKey("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save Interakt key")); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/integrations/interakt`);
      toast.success("Interakt key removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to remove key")); }
  };

  if (!status) return <div className="text-sm text-muted-foreground py-6">Loading integrations…</div>;

  return (
    <div className="border border-border p-6 space-y-4" data-testid="integrations-interakt-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-emerald-50">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Interakt · WhatsApp Business</div>
          <p className="text-xs text-muted-foreground">
            Powers <strong>all outbound WhatsApp messages</strong> — Onboarding Kits, Quote nudges, Dunning ladder, Marketing sequences. Keys are encrypted at rest and never shown after save.
          </p>
        </div>
        <Badge variant="outline"
          className={`rounded-none ${status.configured ? "border-emerald-500/40 text-emerald-700" : "border-foreground/25 text-foreground/60"}`}
          data-testid="integrations-interakt-status-badge">
          {status.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>

      {status.configured && status.updated_at && (
        <div className="text-xs text-muted-foreground border-l-2 border-emerald-500/40 pl-3">
          Last updated {status.updated_at.slice(0, 16).replace("T", " ")}. Current template: <code>{status.template_name || "onboarding_welcome"}</code>.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow mb-1.5 inline-flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Interakt API key
          </Label>
          <Input type="password" value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={status.configured ? "•••••• (paste to replace)" : "Base64-encoded Interakt secret"}
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-interakt-key-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Get one at{" "}
            <a href="https://app.interakt.ai/settings/developer-setting" target="_blank" rel="noreferrer" className="underline">
              Interakt → Developer Settings → API Key
            </a>
            .
          </p>
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">Default WhatsApp template</Label>
          <Input value={tmplName}
            onChange={(e) => setTmplName(e.target.value)}
            placeholder="onboarding_welcome"
            className="h-9 rounded-none"
            data-testid="integrations-interakt-template-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Approved Interakt template name (case-sensitive). Must be pre-approved on Interakt with a matching body-variables count.
          </p>
        </div>
      </div>

      {/* Feb 2026 · Inbound webhook URL — paste this into Interakt dashboard
          so incoming WA messages sync to Comms Inbox and the WA Inbox page. */}
      {status.configured && (
        <InteraktInboundWebhookHint wsId={activeWorkspaceId} />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="integrations-interakt-save">
          <Save className="w-3 h-3 mr-1" />
          {busy ? "Saving…" : (status.configured ? "Replace key" : "Save key")}
        </Button>
        {status.configured && (
          <>
            <InteraktHelloWorldTest wsId={activeWorkspaceId} />
            <TestSendButton provider="interakt" wsId={activeWorkspaceId}
                            testid="integrations-interakt-test" />
          </>
        )}
        {status.configured && (
          <Button onClick={() => setConfirmOpen(true)} variant="ghost" className="rounded-none text-destructive ml-auto"
            data-testid="integrations-interakt-delete">
            <Trash2 className="w-3 h-3 mr-1" /> Remove
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove Interakt key?"
        description="WhatsApp delivery for Onboarding Kits, quote nudges and dunning will stop working until you reconfigure the key."
        confirmLabel="Remove key"
        destructive
        onConfirm={doRemove}
        testid="confirm-remove-interakt"
      />
    </div>
  );
}

// ─────────────── MCUBE (IVR/cloud telephony) ───────────────

function MCubeCard() {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    variant: "cloud",
    api_key: "",
    api_id: "",
    default_caller_did: "",
    webhook_secret: "",
    auto_transcribe: true,
    auto_analyze: true,
    // R57 · Sticky-agent callback routing
    sticky_callback_enabled: true,
    sticky_callback_lookback_days: 30,
    sticky_callback_max_hops: 3,
  });
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Show ?secret=… if the user typed one OR one is already configured.  When
  // a secret is stored server-side but not in the local form (after reload,
  // because we never return the raw value), show a placeholder token so the
  // user knows a secret is in force.
  const secretForUrl = form.webhook_secret || (status?.webhook_secret_set ? "<configured-secret>" : "");
  const webhookUrl = activeWorkspaceId
    ? `${backend}/api/v1/webhooks/mcube/${activeWorkspaceId}${secretForUrl ? `?secret=${encodeURIComponent(secretForUrl)}` : ""}`
    : "";

  const load = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/integrations/mcube`)
      .then((r) => {
        setStatus(r.data);
        setForm((f) => ({
          ...f,
          variant: r.data?.variant || "cloud",
          api_id: r.data?.api_id || "",
          default_caller_did: r.data?.default_caller_did || "",
          auto_transcribe: r.data?.auto_transcribe ?? true,
          auto_analyze: r.data?.auto_analyze ?? true,
          // R57
          sticky_callback_enabled: r.data?.sticky_callback_enabled ?? true,
          sticky_callback_lookback_days: r.data?.sticky_callback_lookback_days ?? 30,
          sticky_callback_max_hops: r.data?.sticky_callback_max_hops ?? 3,
        }));
      })
      .catch(() => setStatus({ configured: false }));
  };
  useEffect(load, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.api_key.trim() || form.api_key.trim().length < 8) {
      toast.error("Paste a valid MCUBE API key (min 8 chars)");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/mcube`, {
        variant: form.variant,
        api_key: form.api_key.trim(),
        api_id: form.api_id.trim() || null,
        default_caller_did: form.default_caller_did.trim() || null,
        webhook_secret: form.webhook_secret.trim() || null,
        auto_transcribe: !!form.auto_transcribe,
        auto_analyze: !!form.auto_analyze,
        // R57 · Sticky-agent callback routing
        sticky_callback_enabled: !!form.sticky_callback_enabled,
        sticky_callback_lookback_days: Math.max(1, Math.min(180, Number(form.sticky_callback_lookback_days) || 30)),
        sticky_callback_max_hops: Math.max(1, Math.min(6, Number(form.sticky_callback_max_hops) || 3)),
      });
      toast.success("MCUBE config saved (API key encrypted at rest)");
      setForm((f) => ({ ...f, api_key: "" }));
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save MCUBE config")); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/integrations/mcube`);
      toast.success("MCUBE config removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to remove config")); }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL copied — paste into MCUBE Call Log URL");
    } catch { toast.error("Clipboard blocked"); }
  };

  if (!status) return <div className="text-sm text-muted-foreground py-6">Loading integrations…</div>;

  return (
    <div className="border border-border p-6 space-y-4" data-testid="integrations-mcube-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-indigo-50">
          <Phone className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">MCUBE · Cloud Telephony</div>
          <p className="text-xs text-muted-foreground">
            Powers the reusable <strong>Call</strong> button on Prospects, Clients, Contacts, Tickets and Onboarding rows. Every completed call is automatically transcribed via Whisper and analysed by Claude for summary, sentiment and next-action.
          </p>
        </div>
        <Badge variant="outline"
          className={`rounded-none ${status.configured ? "border-emerald-500/40 text-emerald-700" : "border-foreground/25 text-foreground/60"}`}
          data-testid="integrations-mcube-status-badge">
          {status.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">Variant</Label>
          <select value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} className="h-9 w-full border border-border px-2 text-sm rounded-none bg-background" data-testid="integrations-mcube-variant">
            <option value="cloud">Cloud (api.mcube.com/Restmcube-api)</option>
            <option value="vmc">VMC (mcube.vmc.in) — legacy</option>
          </select>
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Token (MCube API key)</Label>
          <Input type="password" value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={status.configured ? "•••••• (paste to replace)" : "e.g. eyJhbGc..."}
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-mcube-key-input" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">Account/API ID (optional)</Label>
          <Input value={form.api_id}
            onChange={(e) => setForm({ ...form, api_id: e.target.value })}
            placeholder="acquilix_prod"
            className="h-9 rounded-none"
            data-testid="integrations-mcube-apiid-input" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">Outbound Number (Default caller DID)</Label>
          <Input value={form.default_caller_did}
            onChange={(e) => setForm({ ...form, default_caller_did: e.target.value })}
            placeholder="918899776655"
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-mcube-did-input" />
          <p className="text-[11px] text-muted-foreground mt-1">Used when the agent has no phone number on their HR profile.</p>
        </div>
        <div className="md:col-span-2">
          <Label className="label-eyebrow mb-1.5 inline-block">Webhook secret (recommended)</Label>
          <Input value={form.webhook_secret}
            onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
            placeholder={status.webhook_secret_set ? "•••••• (secret configured — type to replace)" : "A random string — MCUBE must append ?secret=…"}
            className="h-9 rounded-none font-mono text-xs"
            data-testid="integrations-mcube-secret-input" />
          {status.webhook_secret_set && !form.webhook_secret && (
            <p className="text-[11px] text-amber-700 mt-1">
              A secret is already saved but we never expose it — the webhook URL below shows <code>&lt;configured-secret&gt;</code> as a placeholder. Substitute the real value in your MCUBE dashboard, or type a new one here to replace.
            </p>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.auto_transcribe}
            onChange={(e) => setForm({ ...form, auto_transcribe: e.target.checked })}
            data-testid="integrations-mcube-transcribe" />
          <span>Auto-transcribe completed calls (Whisper)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.auto_analyze}
            onChange={(e) => setForm({ ...form, auto_analyze: e.target.checked })}
            data-testid="integrations-mcube-analyze" />
          <span>Auto AI analysis (summary, sentiment, next-action)</span>
        </label>
        {/* R57 · Sticky-agent callback routing */}
        <div className="border-t border-border pt-3 mt-2 space-y-2" data-testid="integrations-mcube-sticky-block">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.sticky_callback_enabled}
              onChange={(e) => setForm({ ...form, sticky_callback_enabled: e.target.checked })}
              data-testid="integrations-mcube-sticky-enabled" />
            <span className="font-semibold">Route callbacks to the last agent who spoke with the customer</span>
          </label>
          <p className="text-[11px] text-muted-foreground pl-6">
            When a customer&apos;s call is missed by your MCube hunt group, auto-dial the customer back <b>from the last agent</b> who successfully called them. Falls through to the next-most-recent agent if that agent also misses, up to <b>Max hops</b> tries.
          </p>
          {form.sticky_callback_enabled && (
            <div className="grid grid-cols-2 gap-3 pl-6">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Look-back window (days)</Label>
                <Input type="number" min={1} max={180}
                  value={form.sticky_callback_lookback_days}
                  onChange={(e) => setForm({ ...form, sticky_callback_lookback_days: e.target.value })}
                  className="h-9 rounded-none" data-testid="integrations-mcube-sticky-lookback" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Max agent hops per callback</Label>
                <Input type="number" min={1} max={6}
                  value={form.sticky_callback_max_hops}
                  onChange={(e) => setForm({ ...form, sticky_callback_max_hops: e.target.value })}
                  className="h-9 rounded-none" data-testid="integrations-mcube-sticky-max-hops" />
              </div>
            </div>
          )}
        </div>
      </div>

      {activeWorkspaceId && (
        <div className="border-l-2 border-indigo-500/40 pl-3 text-[11px] text-muted-foreground space-y-2" data-testid="integrations-mcube-webhook-block">
          <div className="font-semibold text-foreground">Data-Push URLs to configure in MCube portal:</div>
          {[
            ["Call Start URL", "call-start", "Set under 'Action Perform on Call' or similar in the MCube dashboard."],
            ["Call Log URL", "call-log", "Set under 'On Hangup' / 'Call Log Push'."],
            ["Recording URL", "recording", "Set under 'Recording Push' — arrives on the same endpoint."],
          ].map(([label, slug, hint]) => (
            <div key={slug} className="border border-border p-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground text-[11px]">{label}</span>
                <button type="button" onClick={copyWebhook} className="underline text-primary ml-auto"
                         data-testid={`integrations-mcube-webhook-copy-${slug}`}>
                  Copy
                </button>
              </div>
              <code className="block bg-secondary/40 p-1.5 break-all text-[10px]"
                     data-testid={`integrations-mcube-webhook-url-${slug}`}>
                {webhookUrl}
              </code>
              <div className="text-[10px] text-muted-foreground">{hint}</div>
            </div>
          ))}
          <div className="text-[10px] text-muted-foreground italic">
            Paste the same URL into all three fields — our endpoint auto-detects the event type from the payload.
          </div>
        </div>
      )}

      {activeWorkspaceId && status.configured && (
        <McubeAgentDirectory wsId={activeWorkspaceId} />
      )}

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="integrations-mcube-save">
          <Save className="w-3 h-3 mr-1" />
          {busy ? "Saving…" : (status.configured ? "Replace key & save" : "Save config")}
        </Button>
        {status.configured && (
          <Button onClick={() => setConfirmOpen(true)} variant="ghost" className="rounded-none text-destructive ml-auto"
            data-testid="integrations-mcube-delete">
            <Trash2 className="w-3 h-3 mr-1" /> Remove
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove MCUBE config?"
        description="Click-to-call buttons will stop working in this workspace until you reconfigure the API key. Existing call logs stay intact."
        confirmLabel="Remove"
        destructive
        onConfirm={doRemove}
        testid="confirm-remove-mcube"
      />
    </div>
  );
}


/* ── DataForSEO card (Iter 216) ─────────────────────────── */
function DataForSEOCard() {
  const { activeWorkspaceId } = useAuth();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`)
      .then((r) => {
        const list = (r.data && r.data.providers) || r.data || [];
        const p = list.find((x) => x.provider === "dataforseo");
        setStatus(p || null);
      }).catch(() => {});
  }, [activeWorkspaceId]);

  const save = async () => {
    if (!login.trim() || !password.trim()) { toast.error("Login + Password required"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/dataforseo`, {
        api_key: login.trim(), api_secret: password.trim(),
      });
      toast.success("DataForSEO connected · Phase 2 features unlocked");
      setLogin(""); setPassword("");
      const r = await api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`);
      const list = (r.data && r.data.providers) || r.data || [];
      setStatus(list.find((x) => x.provider === "dataforseo") || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Disconnect DataForSEO?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/integrations/dataforseo`);
      toast.success("Disconnected");
      setStatus(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border p-5" data-testid="settings-dataforseo-card">
      <div className="flex items-center gap-3 mb-3">
        <TrendingUp className="w-5 h-5 text-emerald-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>DataForSEO — Rank tracking · keywords · backlinks</div>
            {status?.configured && <Badge className="rounded-none bg-emerald-500/15 text-emerald-700 border-emerald-500/30" variant="outline">Connected</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">Unlocks Phase 2 · SEO rank tracker · keyword volume/difficulty/CPC · SERP features · backlink profile · competitor gap. Sign up at dataforseo.com — grab your <b>API login (email)</b> and <b>API password</b> from the dashboard.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">API login (email)</Label>
          <Input value={login} onChange={(e) => setLogin(e.target.value)} placeholder="me@example.com" className="h-9 rounded-none text-xs" data-testid="dataforseo-login" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">API password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-9 rounded-none text-xs" data-testid="dataforseo-password" />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="dataforseo-save"><Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save"}</Button>
        {status?.configured && <Button variant="outline" onClick={remove} className="rounded-none text-destructive" data-testid="dataforseo-remove"><Trash2 className="w-4 h-4 mr-2" />Disconnect</Button>}
      </div>
    </div>
  );
}

/* ── Google OAuth (GSC + GA4) card (Iter 216) ────────────── */
function GoogleOAuthCard() {
  const { activeWorkspaceId } = useAuth();
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`)
      .then((r) => {
        const list = (r.data && r.data.providers) || r.data || [];
        setStatus(list.find((x) => x.provider === "google_oauth") || null);
      })
      .catch(() => {});
  }, [activeWorkspaceId]);

  const save = async () => {
    if (!clientId.trim() || !secret.trim()) { toast.error("Client ID + secret required"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/google_oauth`, {
        api_key: clientId.trim(), api_secret: secret.trim(),
      });
      toast.success("Google OAuth credentials saved");
      setClientId(""); setSecret("");
      const r = await api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`);
      const list = (r.data && r.data.providers) || r.data || [];
      setStatus(list.find((x) => x.provider === "google_oauth") || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-5" data-testid="settings-google-oauth-card">
      <div className="flex items-center gap-3 mb-3">
        <Search className="w-5 h-5 text-blue-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Google Search Console + Analytics 4</div>
            {status?.configured && <Badge className="rounded-none bg-blue-500/15 text-blue-700 border-blue-500/30" variant="outline">Configured</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            OAuth client from Google Cloud Console. Enable APIs: <code>Search Console API</code> + <code>Analytics Data API</code>.
            Redirect URI: <code>{typeof window !== "undefined" ? window.location.origin : ""}/api/v1/seo/oauth/google/callback</code>.
            Scopes: <code>webmasters.readonly · analytics.readonly</code>.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">OAuth client ID</Label>
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" className="h-9 rounded-none text-xs" data-testid="google-oauth-client-id" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1.5 inline-block">OAuth client secret</Label>
          <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="••••••••" className="h-9 rounded-none text-xs" data-testid="google-oauth-secret" />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="google-oauth-save"><Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

/* ── PageSpeed API key card (Iter 216) ───────────────────── */
function PageSpeedCard() {
  const { activeWorkspaceId } = useAuth();
  const [key, setKey] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`)
      .then((r) => {
        const list = (r.data && r.data.providers) || r.data || [];
        setStatus(list.find((x) => x.provider === "pagespeed") || null);
      })
      .catch(() => {});
  }, [activeWorkspaceId]);

  const save = async () => {
    if (!key.trim()) { toast.error("API key required"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/integrations/pagespeed`, { api_key: key.trim() });
      toast.success("PageSpeed API key saved");
      setKey("");
      const r = await api.get(`/workspaces/${activeWorkspaceId}/marketing/providers`);
      const list = (r.data && r.data.providers) || r.data || [];
      setStatus(list.find((x) => x.provider === "pagespeed") || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-5" data-testid="settings-pagespeed-card">
      <div className="flex items-center gap-3 mb-3">
        <Zap className="w-5 h-5 text-amber-500" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>PageSpeed Insights (optional)</div>
            {status?.configured && <Badge className="rounded-none bg-emerald-500/15 text-emerald-700 border-emerald-500/30" variant="outline">Set</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">Optional — the module works without a key (25k anonymous requests/day) but a key uplifts your quota. Get one at <code>developers.google.com/speed/docs/insights/v5/get-started</code>.</p>
        </div>
      </div>
      <div>
        <Label className="label-eyebrow mb-1.5 inline-block">PageSpeed API key</Label>
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="AIzaSy..." className="h-9 rounded-none text-xs" data-testid="pagespeed-key" />
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="pagespeed-save"><Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

// ─── Feb 2026 · Interakt inbound webhook URL hint ──────────

function InteraktInboundWebhookHint({ wsId }) {
  const [secrets, setSecrets] = useState(null);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/comms/inbound-secrets`)
      .then((r) => setSecrets(r.data?.secrets || []))
      .catch(() => setSecrets([]));
  }, [wsId]);
  const waSecret = secrets?.find((s) => s.channel === "whatsapp");
  if (secrets === null) return null;
  if (!waSecret) return null;
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const fullUrl = `${backend}${waSecret.path}${waSecret.query}`;
  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(val);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 2000);
    } catch { toast.error("Copy failed — highlight and copy manually"); }
  };
  return (
    <div className="border border-amber-400 bg-amber-50/40 p-3 space-y-2" data-testid="integrations-interakt-webhook-hint">
      <div className="text-xs font-semibold text-amber-900 inline-flex items-center gap-1">
        <MessageCircle className="w-3 h-3" /> To sync incoming WhatsApp messages, paste this webhook URL into Interakt
      </div>
      <div className="text-[10px] text-amber-800">
        In Interakt dashboard: <b>Developer Settings → Webhooks</b>. Add this URL as the <b>Message Received</b> webhook.
        Without it, the WhatsApp Inbox will not sync inbound messages.
      </div>
      <div className="flex items-center gap-1.5 border border-border bg-background px-2 py-1">
        <code className="text-[10px] font-mono flex-1 truncate select-all" data-testid="integrations-interakt-webhook-url">{fullUrl}</code>
        <button
          type="button"
          onClick={() => copy(fullUrl)}
          className="text-[10px] px-2 py-0.5 border border-border bg-background hover:bg-muted"
          data-testid="integrations-interakt-webhook-copy"
        >
          {copied === fullUrl ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <div className="text-[10px] text-amber-800">
        <b>Templates:</b> Interakt does not expose a public list-templates API, so approved templates
        must be added manually in{" "}
        <a href="/app/marketing/wa-templates" className="underline">Marketing → WA Template Library</a>.
      </div>
    </div>
  );
}


// ─── Feb 2026 · Mcube Agent Phone Directory ─────────────────────

function McubeAgentDirectory({ wsId }) {
  const [agents, setAgents] = useState([]);
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const [a, m] = await Promise.all([
        api.get(`/workspaces/${wsId}/mcube/agents`),
        api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
      ]);
      setAgents(a.data?.rows || []);
      setMembers(m.data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const toggle = async (agent) => {
    try {
      await api.patch(`/mcube/agents/${agent.id}`, { is_active: !agent.is_active });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const del = async (agent) => {
    if (!confirm(`Remove agent ${agent.display_name} (${agent.agent_number})?`)) return;
    try {
      await api.delete(`/mcube/agents/${agent.id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border p-3 space-y-2" data-testid="mcube-agent-directory">
      <div className="flex items-center gap-2">
        <Phone className="w-3.5 h-3.5 text-primary" />
        <div className="text-sm font-semibold">Agent Phone Directory</div>
        <span className="text-[10px] text-muted-foreground ml-1">
          ({agents.length} configured)
        </span>
        <Button size="sm" className="rounded-none h-7 text-[11px] ml-auto"
                 onClick={() => setShowNew(true)} data-testid="mcube-agent-add">
          <Plus className="w-3 h-3 mr-1" /> Add agent
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        These numbers are used as the <b>exenumber</b> (caller leg) in outbound
        Mcube calls. Each number MUST also be registered inside the Mcube
        dashboard first — Mcube rejects calls from unknown numbers.
      </p>
      {busy && agents.length === 0 ? (
        <div className="text-[11px] text-muted-foreground py-2">Loading…</div>
      ) : agents.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-3 text-center">
          No agents yet. Add at least one so click-to-call has a caller leg to use.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left">
            <tr className="text-[9px] uppercase tracking-widest text-muted-foreground">
              <th className="py-1">Name</th>
              <th className="py-1">Number</th>
              <th className="py-1">Mapped user</th>
              <th className="py-1">Active</th>
              <th className="py-1"></th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`mcube-agent-${a.id}`}>
                <td className="py-1.5 font-semibold">{a.display_name}</td>
                <td className="py-1.5 font-mono text-[11px]">{a.agent_number}</td>
                <td className="py-1.5 text-[11px] text-muted-foreground">
                  {members.find((m) => m.user_id === a.user_id)?.name || a.user_id || "—"}
                </td>
                <td className="py-1.5">
                  <button className={`text-[10px] px-1.5 py-0.5 border ${a.is_active ? "border-emerald-500 text-emerald-700" : "border-slate-400 text-slate-500"}`}
                           onClick={() => toggle(a)} data-testid={`mcube-agent-toggle-${a.id}`}>
                    {a.is_active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="py-1.5 text-right">
                  <button onClick={() => del(a)} className="text-rose-600 hover:underline text-[10px]" data-testid={`mcube-agent-delete-${a.id}`}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && (
        <NewAgentDialog wsId={wsId} members={members}
                         onClose={() => setShowNew(false)}
                         onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function NewAgentDialog({ wsId, members = [], onClose, onCreated }) {
  const [displayName, setDisplayName] = useState("");
  const [agentNumber, setAgentNumber] = useState("");
  const [userId, setUserId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!displayName.trim() || !agentNumber.trim()) { toast.error("Name and number are required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/mcube/agents`, {
        display_name: displayName.trim(),
        agent_number: agentNumber.trim(),
        user_id: userId || null,
        notes: notes.trim() || null,
        is_active: true,
      });
      toast.success("Agent added");
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-background border border-border w-full max-w-sm p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="mcube-agent-new-dialog">
        <div className="font-bold text-sm inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-primary" /> New Mcube agent</div>
        <div>
          <Label className="text-xs font-semibold">Display name *</Label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  autoFocus className="h-9 rounded-none" data-testid="mcube-agent-name" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Phone number (as registered in Mcube) *</Label>
          <Input value={agentNumber} onChange={(e) => setAgentNumber(e.target.value)}
                  placeholder="9823113099" className="h-9 rounded-none font-mono" data-testid="mcube-agent-number" />
          <p className="text-[10px] text-muted-foreground mt-1">The same number can be mapped to multiple users if Mcube treats it as a shared team DID.</p>
        </div>
        <div>
          <Label className="text-xs font-semibold">Mapped user (optional)</Label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
                   className="w-full h-9 border border-border bg-background px-2 text-sm rounded-none"
                   data-testid="mcube-agent-user">
            <option value="">— None —</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
          </select>
          <p className="text-[10px] text-muted-foreground mt-1">Click-to-call auto-selects this user&apos;s agent number when they initiate a call.</p>
        </div>
        <div>
          <Label className="text-xs font-semibold">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Team, working hours…" className="h-9 rounded-none" data-testid="mcube-agent-notes" />
        </div>
        <div className="flex justify-end gap-2">
          <button className="text-xs px-3 py-1.5 border border-border hover:bg-muted" onClick={onClose}>Cancel</button>
          <button className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50"
                   onClick={submit} disabled={busy || !displayName.trim() || !agentNumber.trim()}
                   data-testid="mcube-agent-save">
            {busy ? "Adding…" : "Add agent"}
          </button>
        </div>
      </div>
    </div>
  );
}

